import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { CONTENT_GROUPS, DEFAULT_CONTENT } from "@/content/defaults";
import { apiFetch } from "./api";

export const ContentEditor = ({ token, onAuthError }) => {
    const [values, setValues] = useState(DEFAULT_CONTENT);
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        apiFetch("/site-config")
            .then((data) => {
                setValues({ ...DEFAULT_CONTENT, ...(data.content || {}) });
                setSettings(data.settings || {});
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await apiFetch("/site-config", {
                token,
                method: "PUT",
                body: { content: values, settings },
            });
            toast.success("Content saved", {
                description: "The live site now shows your updates.",
            });
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Save failed", { description: err.message });
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return <p className="py-10 text-center text-sm text-slate-500">Loading…</p>;

    return (
        <div data-testid="admin-content-tab">
            <p className="text-sm text-slate-400">
                Edit the live page copy. Changes go live as soon as you save.
            </p>
            <div className="mt-6 flex flex-col gap-8">
                {CONTENT_GROUPS.map((group) => (
                    <div
                        key={group.title}
                        className="rounded-2xl border border-white/10 bg-[#0C1019] p-6"
                    >
                        <h3 className="font-display text-base font-semibold text-sky-400">
                            {group.title}
                        </h3>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            {group.fields.map((f) => (
                                <div
                                    key={f.key}
                                    className={f.multiline ? "md:col-span-2" : ""}
                                >
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        {f.label}
                                    </label>
                                    {f.multiline ? (
                                        <textarea
                                            data-testid={`content-field-${f.key}`}
                                            rows={3}
                                            value={values[f.key] || ""}
                                            onChange={(e) =>
                                                setValues((v) => ({
                                                    ...v,
                                                    [f.key]: e.target.value,
                                                }))
                                            }
                                            className="mt-1.5 w-full rounded-lg border border-white/15 bg-[#121722] px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-sky-400"
                                        />
                                    ) : (
                                        <input
                                            data-testid={`content-field-${f.key}`}
                                            type="text"
                                            value={values[f.key] || ""}
                                            onChange={(e) =>
                                                setValues((v) => ({
                                                    ...v,
                                                    [f.key]: e.target.value,
                                                }))
                                            }
                                            className="mt-1.5 h-10 w-full rounded-lg border border-white/15 bg-[#121722] px-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-sky-400"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <button
                data-testid="content-save-btn"
                onClick={save}
                disabled={saving}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3 text-sm font-bold text-[#0A0D14] transition-colors hover:bg-sky-300 disabled:opacity-60"
            >
                {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <Save className="h-4 w-4" />
                )}
                {saving ? "Saving…" : "Save All Changes"}
            </button>
        </div>
    );
};
